import { KeytipManager, type IKeytipLayerProps } from '@fluentui/react';
import { addDocumentEventListener } from 'owa-event-listener';

/** Element that had focus prior to entering keytip mode */
let lastActiveElement: typeof document.activeElement;

let removeKeydownListener: ReturnType<typeof addDocumentEventListener>;

/** Restore focus to element that had focus prior to entering keytip mode */
const restoreFocus = () => {
    removeKeydownListener?.();
    lastActiveElement instanceof HTMLElement && lastActiveElement.focus();
};

const onKeyDown = (event: KeyboardEvent) => {
    const { key, target } = event;
    const { inKeytipMode } = KeytipManager.getInstance();

    if (
        // Ensuring target is an `HTMLElement` here to avoid type casting
        !(target instanceof HTMLElement) ||
        inKeytipMode
    ) {
        return;
    }

    const ribbonRoot = document.getElementById('RibbonRoot');
    const isRibbonControl = ribbonRoot?.contains(target);

    if (key === 'Escape' && isRibbonControl) {
        restoreFocus();
    }

    if (!isRibbonControl) {
        removeKeydownListener?.();
    }
};

export const onEnterKeytipMode = (selectedTabId: string) => (): void => {
    // Add keydown listener
    removeKeydownListener = addDocumentEventListener('RibbonKeytipLayer', 'keydown', onKeyDown);

    // Save currently focused element
    lastActiveElement = document.activeElement;

    // Set focus to selected Ribbon tab
    document.getElementById(selectedTabId)?.focus();
};

export const onExitKeytipMode: IKeytipLayerProps['onExitKeytipMode'] = event => {
    // When `event` is `undefined`, it indicates that `keytipManager.exitKeytipMode()` has been called directly.
    // In this case, we want to restore focus.
    if (!event) {
        if (!ignoreNextExitKeytipMode) {
            restoreFocus();
            ignoreNextExitKeytipMode = false;
        }
        return;
    }

    if (!(event instanceof KeyboardEvent && event.target instanceof HTMLElement)) {
        return;
    }

    const {
        key,
        target: { role },
    } = event;

    const isArrowKey =
        key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp';

    const isTabKey = key === 'Tab';

    const isTargetInMenu =
        role === 'gridcell' ||
        role === 'menu' ||
        role === 'menuitem' ||
        role === 'menuitemcheckbox' ||
        role === 'menuitemradio';

    // Avoid restoring last focus when a keytip execution moved focus into a text input
    // (e.g. FontPicker / FontSizePicker ComboBox inputs)
    const activeElement = document.activeElement;
    const isTextInputFocused =
        activeElement?.tagName === 'INPUT' && (activeElement as HTMLInputElement).type === 'text';

    if (
        // Avoid restoring last focus when an `Arrow` key is pressed
        !isArrowKey &&
        // Avoid restoring last focus when `Tab` is pressed
        !isTabKey &&
        // Avoid restoring last focus when current focus is in a dropdown menu
        !isTargetInMenu &&
        // Avoid restoring last focus when a keytip focused a text input
        !isTextInputFocused
    ) {
        restoreFocus();
    }
};

let ignoreNextExitKeytipMode = false;
export const ignoreNextExitKeytipModeOnce: () => void = () => {
    ignoreNextExitKeytipMode = true;
};
